import { ChangeEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';

import { CrossIcon, MplInputV2 } from '@mopla/ui';

import {
	isValidHolderNamePart,
	stripLeadingWhitespace,
} from '../ticketHolderName';

interface ITicketHolderNameFieldProps {
	label: string;
	value: string;
	onChange: (value: string) => void;
}

const TicketHolderNameField: FC<ITicketHolderNameFieldProps> = ({
	label,
	value,
	onChange,
}) => {
	const { t } = useTranslation('ticketshop');
	const hasError = !isValidHolderNamePart(value);
	const [isFocused, setIsFocused] = useState(false);
	const isClearBtnVisible = isFocused && value !== '';

	return (
		<MplInputV2
			type="text"
			label={label}
			value={value}
			onChange={(e: ChangeEvent<HTMLInputElement>) =>
				onChange(stripLeadingWhitespace(e.target.value))
			}
			onFocus={() => setIsFocused(true)}
			onBlur={() => setIsFocused(false)}
			clearable={isClearBtnVisible}
			error={hasError}
			helperText={
				hasError && (
					<Stack
						component="span"
						direction="row"
						alignItems="center"
						spacing={0.5}
					>
						<CrossIcon width={16} height={16} />
						<span>{t('ticket_detail.name_change_section.error_expanded')}</span>
					</Stack>
				)
			}
		/>
	);
};

interface ITicketHolderNameChangeContentProps {
	firstName: string;
	lastName: string;
	onFirstNameChange: (value: string) => void;
	onLastNameChange: (value: string) => void;
}

export const TicketHolderNameChangeContent: FC<
	ITicketHolderNameChangeContentProps
> = ({ firstName, lastName, onFirstNameChange, onLastNameChange }) => {
	const { t } = useTranslation('ticketshop');

	return (
		<Stack spacing={2} paddingTop={2} alignSelf="stretch">
			<TicketHolderNameField
				label={t('ticket_detail.name_change_section.first_name')}
				value={firstName}
				onChange={onFirstNameChange}
			/>
			<TicketHolderNameField
				label={t('ticket_detail.name_change_section.last_name')}
				value={lastName}
				onChange={onLastNameChange}
			/>
		</Stack>
	);
};
